// redux/slices/whatsappConnectSlice.js
// Подключение WhatsApp Business мастера через Embedded Signup v4
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { resetStore } from "./resetStore";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

// Сервер отвечает { error, details } — достаём понятную строку
const getError = (error, fallback) => {
  const data = error?.response?.data;
  const msg = data?.details || data?.error || data?.message || error?.message || fallback;
  return typeof msg === "string" ? msg : JSON.stringify(msg);
};

/* ========================= THUNKS ========================= */

// Статус подключения текущего мастера (uid берётся на сервере из сессии)
export const fetchWhatsAppConnectStatus = createAsyncThunk(
  "whatsappConnect/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("GET", "/whatsapp-connect/status");
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "fetchWhatsAppConnectStatus failed"));
    }
  }
);

// Обмен code из Embedded Signup на токен + онбординг номера
export const exchangeWhatsAppCode = createAsyncThunk(
  "whatsappConnect/exchange",
  async ({ code, event, waba_id, phone_number_id, business_id }, { rejectWithValue }) => {
    try {
      if (!code) throw new Error("code не передан");

      const response = await apiRequest("POST", "/whatsapp-connect/exchange", {
        code,
        event,
        waba_id,
        phone_number_id,
        business_id,
      });

      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "exchangeWhatsAppCode failed"));
    }
  }
);

// Отключение
export const disconnectWhatsApp = createAsyncThunk(
  "whatsappConnect/disconnect",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("DELETE", "/whatsapp-connect");
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "disconnectWhatsApp failed"));
    }
  }
);

// Шаблоны мастера: стандартные тексты + что уже есть в его WABA
export const fetchWhatsAppTemplates = createAsyncThunk(
  "whatsappConnect/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("GET", "/whatsapp-connect/templates");
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "fetchWhatsAppTemplates failed"));
    }
  }
);

// Сохранить: создать недостающие + изменить отредактированные. overrides = { [name]: { [lang]: text } }
export const saveWhatsAppTemplates = createAsyncThunk(
  "whatsappConnect/saveTemplates",
  async ({ overrides = {} } = {}, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/whatsapp-connect/templates", { overrides });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "saveWhatsAppTemplates failed"));
    }
  }
);

// Готовность к активации: шаблоны + оплата + номер
export const fetchWhatsAppReadiness = createAsyncThunk(
  "whatsappConnect/fetchReadiness",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest("GET", "/whatsapp-connect/readiness");
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "fetchWhatsAppReadiness failed"));
    }
  }
);

// Тестовая отправка шаблона с номера мастера на указанный номер
export const sendWhatsAppTestMessage = createAsyncThunk(
  "whatsappConnect/sendTestMessage",
  async ({ name, language, phone }, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/whatsapp-connect/test-send", { name, language, phone });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "sendWhatsAppTestMessage failed"));
    }
  }
);

// Оплата подключения (единоразово): сервер создаёт ссылку AllPay ZmanUp
export const startWhatsAppFeeCheckout = createAsyncThunk(
  "whatsappConnect/startFeeCheckout",
  async ({ lang, returnPath } = {}, { rejectWithValue }) => {
    try {
      const response = await apiRequest("POST", "/whatsapp-connect/checkout", { lang, returnPath });
      return response?.data || response;
    } catch (error) {
      return rejectWithValue(getError(error, "startWhatsAppFeeCheckout failed"));
    }
  }
);

/* ========================= SELECTORS ========================= */

export const selectWhatsAppConnect = (state) => state.whatsappConnect;

/* ========================= STATE ========================= */

const initialState = {
  connection: null, // { connected, status, displayPhoneNumber, verifiedName, isCoexistence, ... }
  statusLoading: false,
  statusLoaded: false,
  connecting: false,
  disconnecting: false,
  error: null,

  // шаблоны
  templates: null, // { footer, limits, templates: [...] }
  templatesLoading: false,
  templatesSaving: false,
  templatesResults: null, // результаты последнего сохранения
  templatesError: null,

  // готовность к активации
  readiness: null, // { ready, templates, payment, canSendMessage, otherErrors, links }
  readinessLoading: false,
  readinessError: null,

  // оплата подключения
  feeCheckoutLoading: false,
  feeCheckoutError: null,

  // тестовая отправка
  testSending: false,
  testResult: null, // { ok, to, messageId }
  testError: null,
};

/* ========================= SLICE ========================= */

const whatsappConnectSlice = createSlice({
  name: "whatsappConnect",
  initialState,
  reducers: {
    clearWhatsAppConnectError: (state) => {
      state.error = null;
    },
    clearWhatsAppTemplatesResults: (state) => {
      state.templatesResults = null;
      state.templatesError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWhatsAppConnectStatus.pending, (state) => {
        state.statusLoading = true;
      })
      .addCase(fetchWhatsAppConnectStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.statusLoaded = true;
        state.connection = action.payload || null;
      })
      .addCase(fetchWhatsAppConnectStatus.rejected, (state, action) => {
        state.statusLoading = false;
        state.statusLoaded = true;
        state.error = action.payload || action.error?.message || "fetchWhatsAppConnectStatus failed";
      })

      .addCase(exchangeWhatsAppCode.pending, (state) => {
        state.connecting = true;
        state.error = null;
      })
      .addCase(exchangeWhatsAppCode.fulfilled, (state, action) => {
        state.connecting = false;
        const p = action.payload || {};
        state.connection = {
          ...(state.connection || {}),
          connected: true,
          status: p.status || "pending",
          source: "embedded",
          displayPhoneNumber: p.displayPhoneNumber || null,
          verifiedName: p.verifiedName || null,
          isCoexistence: !!p.isCoexistence,
        };
      })
      .addCase(exchangeWhatsAppCode.rejected, (state, action) => {
        state.connecting = false;
        state.error = action.payload || action.error?.message || "exchangeWhatsAppCode failed";
      })

      .addCase(disconnectWhatsApp.pending, (state) => {
        state.disconnecting = true;
        state.error = null;
      })
      .addCase(disconnectWhatsApp.fulfilled, (state) => {
        state.disconnecting = false;
        state.connection = { ...(state.connection || {}), connected: false, status: "revoked" };
      })
      .addCase(disconnectWhatsApp.rejected, (state, action) => {
        state.disconnecting = false;
        state.error = action.payload || action.error?.message || "disconnectWhatsApp failed";
      })

      .addCase(fetchWhatsAppTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.templatesError = null;
      })
      .addCase(fetchWhatsAppTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload || null;
      })
      .addCase(fetchWhatsAppTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.templatesError = action.payload || action.error?.message || "fetchWhatsAppTemplates failed";
      })

      .addCase(saveWhatsAppTemplates.pending, (state) => {
        state.templatesSaving = true;
        state.templatesError = null;
        state.templatesResults = null;
      })
      .addCase(saveWhatsAppTemplates.fulfilled, (state, action) => {
        state.templatesSaving = false;
        const { results, ...data } = action.payload || {};
        state.templatesResults = results || [];
        if (data?.templates) state.templates = data;
      })
      .addCase(saveWhatsAppTemplates.rejected, (state, action) => {
        state.templatesSaving = false;
        state.templatesError = action.payload || action.error?.message || "saveWhatsAppTemplates failed";
      })

      .addCase(fetchWhatsAppReadiness.pending, (state) => {
        state.readinessLoading = true;
        state.readinessError = null;
      })
      .addCase(fetchWhatsAppReadiness.fulfilled, (state, action) => {
        state.readinessLoading = false;
        state.readiness = action.payload || null;
      })
      .addCase(fetchWhatsAppReadiness.rejected, (state, action) => {
        state.readinessLoading = false;
        state.readinessError = action.payload || action.error?.message || "fetchWhatsAppReadiness failed";
      })

      .addCase(sendWhatsAppTestMessage.pending, (state) => {
        state.testSending = true;
        state.testResult = null;
        state.testError = null;
      })
      .addCase(sendWhatsAppTestMessage.fulfilled, (state, action) => {
        state.testSending = false;
        state.testResult = action.payload || null;
      })
      .addCase(sendWhatsAppTestMessage.rejected, (state, action) => {
        state.testSending = false;
        state.testError = action.payload || action.error?.message || "sendWhatsAppTestMessage failed";
      })

      .addCase(startWhatsAppFeeCheckout.pending, (state) => {
        state.feeCheckoutLoading = true;
        state.feeCheckoutError = null;
      })
      .addCase(startWhatsAppFeeCheckout.fulfilled, (state) => {
        // остаётся true: страница уходит на AllPay
        state.feeCheckoutLoading = true;
      })
      .addCase(startWhatsAppFeeCheckout.rejected, (state, action) => {
        state.feeCheckoutLoading = false;
        state.feeCheckoutError = action.payload || action.error?.message || "startWhatsAppFeeCheckout failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const { clearWhatsAppConnectError, clearWhatsAppTemplatesResults } = whatsappConnectSlice.actions;

export default whatsappConnectSlice.reducer;